import { interpolate } from "gsap";
import { Select } from "../select";
import { Multiselect } from "../multiselect-dropdown";
import { Datepicker } from "../datepicker";
import { ConfiguratorFieldDepencencyController } from "../configurator-field-dependency-controller";

import { getAccessoriesByType, setAccessoriesInitialSection } from './helpers/accessories';
import { deleteNewAccessorySection, setAccessoriesDefaultOption, setContainer, setFormSelect, setLabel, setSelectEl, setSitecorebasedOptions } from './helpers/setInput';
import { clearAccessorySummary, updateSummaryOnInputChange } from "./helpers/updateSummaryOnInputChange";
import { inputTemplateIDs } from './helpers/inputTemplateIDs';
import { dateOfPatientFittingDatepickerSetup, defaultDatepickerSetup } from './helpers/inputsSetups';
import { fetchProductData, isNullOrWhitespace, getQueryStringParameterByName } from '../../Foundation/utils';
import { getDefaultSelectValue } from './helpers/inputsSetups';

export class Configurator {
    constructor(element, callbackAfterConfiguratorValidation) {
        this._refs = {
            el: element,
        };

        this.configurationMode = "";
        this.configurationAction = "";
        this.configuratorId = "";
        this.serialNumber = "";
        this.fields = [];

        this.fieldValidationState = [];
        this.isAllFieldValidationsValid = false;

        this.callbackAfterConfiguratorValidation = callbackAfterConfiguratorValidation;

        this.fieldsDependsOnMe = [];

        this.fieldsDependencyController = new ConfiguratorFieldDepencencyController()

    }

    //create configurator from cart line - take the id of configurator as additional parameter
    async createConfiguratorWithValue(serialNumber, mode, action, configuratorId) {
        this.serialNumber = serialNumber;
        this.configurationMode = mode;
        this.configurationAction = action;
        this.configuratorId = configuratorId;
        let apiData = await this.getConfiguratorDataWithValue(configuratorId);


        // Get sections
        apiData.ConfiguratorSections.forEach((section) => {
            this.setupSection(section);
        });

        this.fieldsDependencyController.createDepdencyMap();

        this.recheckValidationOnConfigurator();
    }

    async createConfigurator(serialNumber, name, mode, action) {
        this.serialNumber = serialNumber;
        this.configurationMode = mode;
        this.configurationAction = action;
        let apiData = await this.getConfiguratorData(name);

        // set id
        this.configuratorId = apiData.Id;

        // Get sections
        apiData.ConfiguratorSections.forEach((section) => {
            this.setupSection(section);
        });

        this.fieldsDependencyController.createDepdencyMap();

        this.recheckValidationOnConfigurator();
    }

    renderServerErrors(errorsArray) {
        let fields = this.fields;

        errorsArray.forEach((errorFromServer) => {
            let field = fields.find((field) => field.Id === errorFromServer.FieldId);
            if(field.renderServerError){
                field.renderServerError(errorFromServer);
            }else{
                console.error("unable to render server error", errorFromServer);
            }
        });
    }

    recheckValidationOnConfigurator() {

        let allValid = true;
        this.fieldValidationState.forEach((validationObject) => {
            if (validationObject.isValid !== true) {
                allValid = false;
            }
        });

        this.isAllFieldValidationsValid = allValid;

        (() => { this.callbackAfterConfiguratorValidation(); })();
    }

    setupSection(section) {
        // Section container
        var sectionContainer = document.createElement("div");
        sectionContainer.classList.add("clearfix");
        sectionContainer.classList.add("configurator__section");
        sectionContainer.classList.add("visible");
        sectionContainer.dataset.configuratorSerialNo = this.serialNumber;
        this._refs.el.appendChild(sectionContainer);

        // Section heading
        var sectionHeading = document.createElement("div");
        if (section.DisplayName) {
            sectionHeading.classList.add("configurator_reason__selector", "configurator_reason__title", "bold-font");
            sectionHeading.innerHTML = section.DisplayName;
            sectionContainer.appendChild(sectionHeading);
        }
        // Section help text
        var sectionHelpText = document.createElement("p");
        sectionHelpText.innerHTML = section.HelpText;
        sectionContainer.appendChild(sectionHelpText);

        // Fields
        this.setupFields(section.ConfiguratorFields, sectionContainer);
    }

    //fields based on Sitecore data
    setupFields(fields, container) {
        // Order field from SortOrder
        fields.sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));
        const { accessoriesTemplateID, checkboxTemplateID, datepickerTemplateID, dropdownTemplateID, fieldUploadTemplateID, 
                multiselectorTemplateID, textFieldTemplateID, textareaTemplateID } = inputTemplateIDs;
        fields.forEach((field) => {
            switch (field.TemplateId) {
                case textFieldTemplateID:
                    this.createInputTextField(field, container);
                    break;
                case datepickerTemplateID:
                    this.createInputDateField(field, container);
                    break;
                case checkboxTemplateID:
                    this.createInputCheckboxField(field, container);
                    break;
                case textareaTemplateID:
                    this.createInputTextAreaField(field, container);
                    break;
                case multiselectorTemplateID:
                    this.createCategorizedMultiselector(field, container);
                    break;
                case dropdownTemplateID:
                    this.createDropDown(field, container);
                    break;
                case fieldUploadTemplateID:
                    this.createFileUploadField(field, container);
                    break;
                case accessoriesTemplateID:
                    this.createAccessoriesField(field, container);
                    break;
            }
        });
    }

    setValidationStateBasedOnVisibility(field, hidden) {
        const found = this.fieldValidationState.find(
            (element) => element.fieldId === field.Id
        );
        if (found) {
            found.isHidden = hidden;
        }

        this.recheckValidationOnConfigurator();
    }

    setValidationOnField({ field, htmlInput, htmlContainer, htmlValidationMessage, validateOnEvent, validateFunction, requiredOverride = null, validAsDefault = null, that }) {
        let required = field.IsRequired;
        let thisRef = that || this;
        function updateComponent(validationResult, errorMessage) {
            if (!Array.isArray(htmlInput)) {
                htmlInput.setAttribute("validation-valid", validationResult);
            }
            htmlContainer.setAttribute("validation-valid", validationResult);

            setValidationState(validationResult);
            thisRef.recheckValidationOnConfigurator();

            // show validation error message
            if (validationResult === false) {
                htmlValidationMessage.innerHTML = errorMessage;
                htmlValidationMessage.style.display = "block";
            } else {
                htmlValidationMessage.style.display = "none";
            }
        }

        function setValidationState(validationResult) {
            // set global validation state
            const found = thisRef.fieldValidationState.find(
                (element) => element.fieldId === field.Id
            );
            found._isValid = validationResult;
        }

        // define renderServerError method even if not required - if server returns invalid for some reason, we need to make sure to render the server error.
        field.renderServerError = (errorFromServer) => {
            updateComponent(false, errorFromServer.ErrorMessage);
        };


        if (requiredOverride !== null) {
            required = requiredOverride;
        }

        if (required) {
            const t = that || this;
            t.fieldValidationState = t.fieldValidationState.filter(s => s.fieldId !== field.Id);
            t.fieldValidationState.push({
                fieldId: field.Id,
                // null as inital, this will make sure so we do not show error message
                _isValid: null,
                isHidden: false,
                get isValid() {
                    if (this.isHidden) {
                        return true;
                    }
                    else {
                        return this._isValid;
                    }
                }
            });

            htmlContainer.setAttribute("validation-required", true);
            if (Array.isArray(htmlInput)) {
                htmlInput.forEach((input) => {
                    input.addEventListener(validateOnEvent, (e) => {
                        const validationResult = validateFunction(e.currentTarget, field);
                        updateComponent(validationResult, field.RequiredFieldErrorMessage);
                    });
                });
            } else {
                htmlInput.setAttribute("validation-required", true);
                htmlInput.addEventListener(validateOnEvent, (e) => {
                    const validationResult = validateFunction(e.currentTarget, field);
                    updateComponent(validationResult, field.RequiredFieldErrorMessage);
                });
            }     

          
            var inputToValidate = Array.isArray(htmlInput) ? htmlInput[0] : htmlInput;

            // do initial validation without showing errors
            const validationResult = validateFunction(inputToValidate, field);

            setValidationState(validationResult);
        }
    }

    async createAccessoriesField(field, sectionContainer) {
        const apiKey = document.getElementById("UserIdAPIKeyContainer").innerHTML.trim();
        const warrantyApiUrl = document.getElementById("WarrantyAPIUrlContainer").innerHTML.trim();
        const userId = document.getElementById("UserIdContainer").innerHTML.trim();
        const serialNumber = getQueryStringParameterByName("id");
        const accessoryType = field.AccessoriesType;
        const modelNo = await fetchProductData({ apiKey, serialNumber, userId, warrantyApiUrl }).then(d => d.modelNo);
        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        const allOptions = await getAccessoriesByType({ accessoryType, modelNo });
        const id = `${sectionID}-newAccessorySelect`;
        let filteredAccesories = allOptions;
        let filterValues = [];

        const createSelectSection = (options) => setAccessoriesInitialSection({ 
            field,
            id,
            options, 
            sectionContainer,
            setValidationOnField: this.setValidationOnField,
            setDependencyOnField: this.fieldsDependencyController.setDependencyOnField,
            setValidationStateBasedOnVisibility: this.setValidationStateBasedOnVisibility,
            that: this
        });

        field.Filters.forEach((filter, index) => {
            const fieldID = field.Id;
            const name = filter.Name;
            const nameBasedID = `${name.replaceAll(' ', '')}-${sectionID}`;
            const fieldLabel = document.createElement("label");
            const inputName = `AccessoriesField-${fieldID}-${nameBasedID}`;
            
            const container = document.createElement("div");
            const formSelect = document.createElement("div");
            const selectEl = document.createElement("select");
            const defaultOption = document.createElement("option");
            const options = filter.Values;
            var values = [...new Set(allOptions.map(x => x.CustomProperties.filter(y => y.Name == filter.FilterName).map(y => y.Value)).flat())];;
            const filterName = filter.FilterName;
            const validOptions  = options.filter(x => values.includes(x.Value));
      
            setContainer({ container, id: `filterInput-${index}`, sectionContainer, uniqueClass: 'filterInput' });
            setLabel({ container, fieldLabel, inputName, name });
            setFormSelect({ container, formSelect, uniqueValue: nameBasedID });
            setSelectEl({ fieldID, formSelect, id: nameBasedID, selectEl });
            setAccessoriesDefaultOption({ defaultOption, displayedText: filter.TextInDropDown, selectEl });
            setSitecorebasedOptions({ options: validOptions, selectEl });

            new Select(formSelect);

            const filterEl = document.getElementById(nameBasedID);

            filterEl.addEventListener('change', (e) => {
                const v = e.target.value;
                deleteNewAccessorySection({ id, that: this });

                if (v) {
                    filterValues = filterValues.filter(value => value.FilterName !== filterName);
                    filterValues.push({ FilterName: filterName, Value: v });
                    filteredAccesories = allOptions.filter(o => {
                        return o.CustomProperties.find(i => i.Name === filterName && i.Value === v);
                    })
                } else {
                    filterValues = filterValues.filter((fV) => fV.FilterName !== filter.FilterName);
                    filteredAccesories = allOptions;
                }

                clearAccessorySummary({ field, sectionID });

                if (filterValues?.length > 0) {
                    let tempValues = [];
                    filterValues.forEach(fV => {
                        tempValues.push(filteredAccesories.filter(o => {
                            return o.CustomProperties.find(i => i.Name === fV.FilterName && i.Value === fV.Value);
                        }));
                    });
                    filteredAccesories = [...new Set(...tempValues)];
                }
                createSelectSection(filteredAccesories);
                const input = sectionContainer.querySelector('.newSelect');

                updateSummaryOnInputChange({ field, input, sectionID });
            });
        });
        
        createSelectSection(filteredAccesories);
        
        const input = sectionContainer.querySelector('.newSelect');
        updateSummaryOnInputChange({ field, input, sectionID });
    }

    createFileUploadField(field, sectionContainer) {
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection", "configurator__fileupload");
        sectionContainer.appendChild(container);
        var inputLabel = document.createElement("label");
        const serialNo = sectionContainer.getAttribute('data-configurator-serial-no');
        var inputName = "fileinput-" + `${field.Id}-${serialNo}`; //serialNo to handle when both serveral products and different repair reasons are choosen
        inputLabel.setAttribute("for", inputName);

        if (this.configurationMode === "new" && field.DisplayName.indexOf(":") > -1) {
            inputLabel.innerHTML = field.DisplayName.split(":")[0];
        } else {
            inputLabel.innerHTML = field.DisplayName;
        }

        container.appendChild(inputLabel);

        var fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.className = "configurator__input option";
        fileInput.id = inputName;
        fileInput.setAttribute("name", inputName);
        fileInput.setAttribute("data-fieldid", field.Id);

        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        updateSummaryOnInputChange({ field, input: fileInput, sectionID });

        if (field.FileName && this.configurationMode == "edit") {
            fileInput.samefile = true;
            fileInput.samefileOrig = true;
        }
        container.appendChild(fileInput);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        fileInput.addEventListener("input", (event) => {
            if (event.currentTarget.files.length > 0) {
                event.currentTarget.samefile = false;
                var reader = new FileReader();
                var file = event.currentTarget.files[0];
                reader.readAsDataURL(file);
                reader.onload = function () {
                    file.content = reader.result;
                };
            } else {
                event.currentTarget.samefile = event.currentTarget.samefileOrig;
            }
        })

        let thisRef = this;

        this.setValidationOnField({
            field,
            htmlInput: fileInput,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            // change event runs after input - see above
            validateOnEvent: "change",
            validateFunction: (currentTarget) => {
                if (currentTarget.samefile && thisRef.configurationMode === "edit" && currentTarget.files.length === 0) {
                    return true;
                }
                return currentTarget.files.length === 1;
            },
            requiredOverride: field.Required,
            validAsDefault: fileInput.samefile
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: fileInput,
            controlToHide: container,
            triggerOnEvent: "change",
            getValueCallback: (currentField) => {
                return currentField.inputs[0].files.length === 1;
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });

        field.inputs = [fileInput];
        this.fields.push(field);
    }


    createInputDateField(field, sectionContainer) {
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection", "configurator__datefield");
        sectionContainer.appendChild(container);
        var inputLabel = document.createElement("label");
        var inputName = "dateinput-" + field.Id;
        inputLabel.setAttribute("for", inputName);
        inputLabel.innerHTML = field.DisplayName;

        container.appendChild(inputLabel);

        var divContainer = document.createElement("div");
        divContainer.className = "configurator__datefield-container";

        var dateInput = document.createElement("input");
        dateInput.type = "text";
        dateInput.className = "configurator__input option form-control";
        dateInput.id = inputName;
        dateInput.setAttribute("name", inputName);
        dateInput.setAttribute("data-fieldid", field.Id);
        const isDateOfPatientFitting = Boolean(document.getElementById('register-configurators')); //check if current action is Register warranty and there is only one datepicker. It's not an elegant solution, but for now there is no Sitecore ID to identify 'Date of patient fitting' field regardless of language & value. To modify in case if in the future Register warranty would need more than one datepicker with different condition.
        const datepickerSetup = isDateOfPatientFitting ? dateOfPatientFittingDatepickerSetup : defaultDatepickerSetup;
        dateInput.setAttribute('data-datepicker', JSON.stringify(datepickerSetup));
        if (field.Date && this.configurationMode == "edit") {
            let parsedDate = new Date(Date.parse(field.Date));           
            var year = parsedDate.getFullYear();            
            var month = (1 + parsedDate.getMonth()).toString();
            month = month.length > 1 ? month : '0' + month;            
            var day = parsedDate.getDate().toString();
            day = day.length > 1 ? day : '0' + day;
            dateInput.value = month + '-' + day + '-' + year;;
        }
        divContainer.appendChild(dateInput);

        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        updateSummaryOnInputChange({ field, input: dateInput, sectionID });

        var i = document.createElement("i");
        i.className = "eva eva-calendar-outline configurator__datefield-icon";
        i.setAttribute("aria-hidden", true);
        divContainer.appendChild(i);

        container.appendChild(divContainer);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        this.setValidationOnField({
            field,
            htmlInput: dateInput,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "change",
            validateFunction: (currentTarget) => {
                return currentTarget.value.length > 0;
            }
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: dateInput,
            controlToHide: container,
            triggerOnEvent: "change",
            getValueCallback: (currentField) => {
                return currentField.inputs[0].value;
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });

        field.inputs = [dateInput];
        this.fields.push(field);
        var testDate = new Datepicker(dateInput);
    }

    createInputTextField(field, sectionContainer) {
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection");
        sectionContainer.appendChild(container);
        var inputLabel = document.createElement("label");
        var inputName = "textinput-" + field.Id;
        inputLabel.setAttribute("for", inputName);
        inputLabel.innerHTML = field.DisplayName;

        container.appendChild(inputLabel);

        var textInput = document.createElement("input");
        textInput.type = "text";
        textInput.className = "configurator__input option form-control";
        textInput.id = inputName;
        textInput.setAttribute("name", inputName);
        textInput.setAttribute("data-fieldid", field.Id);

        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        updateSummaryOnInputChange({ field, input: textInput, sectionID });

        if (field.Text && this.configurationMode == "edit") {
            textInput.value = field.Text;
        }
        container.appendChild(textInput);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        this.setValidationOnField({
            field,
            htmlInput: textInput,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "input",
            validateFunction: (currentTarget) => {
                return currentTarget.value.length > 0;
            }
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: textInput,
            controlToHide: container,
            triggerOnEvent: "input",
            getValueCallback: (currentField) => {
                return currentField.inputs[0].value;
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });


        field.inputs = [textInput];
        this.fields.push(field);
    }

    createInputTextAreaField(field, sectionContainer) {
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection");
        sectionContainer.appendChild(container);
        var inputLabel = document.createElement("label");
        var inputName = "textarea-" + field.Id;
        inputLabel.setAttribute("for", inputName);
        inputLabel.innerHTML = field.DisplayName;

        container.appendChild(inputLabel);

        var textarea = document.createElement("textarea");
        textarea.className = "configurator__textarea option form-control";
        textarea.id = inputName;
        textarea.setAttribute("name", inputName);
        textarea.setAttribute("rows", "2");
        textarea.setAttribute("cols", "20");
        textarea.setAttribute("data-fieldid", field.Id);

        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        updateSummaryOnInputChange({ field, input: textarea, sectionID });

        if (field.Text && this.configurationMode == "edit") {
            textarea.value = field.Text;
        }
        container.appendChild(textarea);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        this.setValidationOnField({
            field,
            htmlInput: textarea,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "input",
            validateFunction: (currentTarget) => {
                return currentTarget.value.length > 0;
            }
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: textarea,
            controlToHide: container,
            triggerOnEvent: "input",
            getValueCallback: (currentField) => {
                return currentField.inputs[0].value;
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });

        field.inputs = [textarea];
        this.fields.push(field);
    }

    createInputCheckboxField(field, sectionContainer) {
        //  div
        //      label
        //      span
        //          input
        //          label
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection");
        sectionContainer.appendChild(container);

        var fieldLabel = document.createElement("label");
        var inputName = "checkboxinput-" + field.Id + "-" + this.serialNumber;
        fieldLabel.setAttribute("for", inputName);
        fieldLabel.classList.add("configurator__label");
        fieldLabel.innerHTML = "";

        container.appendChild(fieldLabel);

        var span = document.createElement("span");
        span.className = "configuratorcheckbox option";
        container.appendChild(span);

        var checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.className = "configurator__input option form-control";
        checkbox.id = inputName;
        checkbox.setAttribute("name", inputName);
        checkbox.setAttribute("data-fieldid", field.Id);
        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');

        updateSummaryOnInputChange({ field, input: checkbox, sectionID });

        if (field.Checked && this.configurationMode == "edit") {
            checkbox.checked = field.Checked;
        }
        span.appendChild(checkbox);

        var checkBoxLabel = document.createElement("label");
        checkBoxLabel.setAttribute("for", inputName);
        checkBoxLabel.innerHTML = field.DisplayName;

        span.appendChild(checkBoxLabel);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);
        if (field.FeatureKey === 'ExtraRepairPeriodValueForRemake' || 
            field.FeatureKey === 'ExtraRepairPeriodValueForRepair'
        ) {
            checkbox.setAttribute('data-featurekey', 'ExtraRepairPeriodValue');
        }

        this.setValidationOnField({
            field,
            htmlInput: checkbox,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "input",
            validateFunction: (currentTarget) => {
                return currentTarget.checked;
            },
            // checkboxes can never be required, but add validation to handle server errors
            requiredOverride: false
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: checkbox,
            controlToHide: container,
            triggerOnEvent: "input",
            getValueCallback: (currentField) => {

                return !currentField.inputs[0].dataset.usevaluefromcheckbox ? currentField.inputs[0].checked.toString() : currentField.inputs[0].value;
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });

        field.inputs = [checkbox];
        this.fields.push(field);
    }

    createCategorizedMultiselector(field, sectionContainer) {
        let dataGroupCode = field.Name;
        if (field.NavisionGroupCode) {
            dataGroupCode = field.NavisionGroupCode;
        }

        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection");
        container.classList.add("configurator__multiselect");
        sectionContainer.appendChild(container);

        var subsectionLabel = document.createElement("div");
        subsectionLabel.innerText = field.CategoryLabel;
        subsectionLabel.classList.add("configurator__multiselect-mainlabel");
        container.appendChild(subsectionLabel);

        let formSelect = document.createElement("div");
        formSelect.classList.add("form-select");
        formSelect.setAttribute("data-select", "");
        container.appendChild(formSelect);

        // select
        let selectEl = document.createElement("select");
        selectEl.classList.add("configuratordropdown");
        selectEl.classList.add("option");
        selectEl.setAttribute("data-groupcode", dataGroupCode);

        // sideids?
        selectEl.setAttribute("id", "MultiselectCategory");
        selectEl.setAttribute("data-id", "MultiselectCategory");
        formSelect.appendChild(selectEl);

        // select options
        // Default
        let defaultOption = document.createElement("option");
        defaultOption.setAttribute("selected", "selected");
        defaultOption.setAttribute("value", "");
        defaultOption.innerHTML = field.EmptyCategoryLabel;
        selectEl.appendChild(defaultOption);

        field.ConfigurationFieldOptions.forEach((option) => {
            let optionEl = document.createElement("option");
            optionEl.classList.add("option");
            optionEl.setAttribute("value", option.Name);
            optionEl.innerHTML = option.DisplayName;
            optionEl.setAttribute("data-valuecode", option.Id);
            selectEl.appendChild(optionEl);
        });

        let multiselectWrapper = document.createElement("div");
        multiselectWrapper.classList.add("configurator__multiselect-wrapper");

        container.appendChild(multiselectWrapper);

        let multiSelectLeftBox = document.createElement("div");
        multiSelectLeftBox.classList.add("configurator__multiselect-wrapper-half");
        multiselectWrapper.appendChild(multiSelectLeftBox);
        let leftBoxLabel = document.createElement("div");
        leftBoxLabel.classList.add("configurator__multiselect-label");
        leftBoxLabel.innerHTML =
            field.ChildsLabel + ' <span class="shownOptionsCount"></span>';
        multiSelectLeftBox.appendChild(leftBoxLabel);
        let checkboxesContainer = document.createElement("div");
        checkboxesContainer.classList.add("configurator__multiselect-checkboxes");
        multiSelectLeftBox.appendChild(checkboxesContainer);
        let emptyDescriptionLeft = document.createElement("p");
        emptyDescriptionLeft.classList.add("configurator__multiselect-description");
        emptyDescriptionLeft.innerHTML = field.EmptyChildsLabel;
        checkboxesContainer.appendChild(emptyDescriptionLeft);
        // add the checkboxes
        let multiselectOptions = document.createElement("span");
        multiselectOptions.setAttribute("id", "MultiselectOption");
        checkboxesContainer.appendChild(multiselectOptions);
        let inputs = [];
        field.ConfigurationFieldOptions.forEach((option) => {
            option.Children.forEach((checkBox) => {
                let checkboxContainer = document.createElement("span");
                checkboxContainer.classList.add("option");
                checkboxContainer.setAttribute("data-parent-category", option.Id);
                checkboxContainer.setAttribute("data-id", "MultiselectOption");
                checkboxContainer.setAttribute("data-valuecode", checkBox.Name);

                multiselectOptions.appendChild(checkboxContainer);
                let checkboxInput = document.createElement("input");
                checkboxInput.setAttribute("type", "checkbox");
                checkboxInput.setAttribute("value", checkBox.Id);
                checkboxInput.setAttribute("id", this.serialNumber + checkBox.Id);
                checkboxInput.setAttribute("data-fieldid", field.Id);
                checkboxInput.setAttribute("data-usevaluefromcheckbox", true);

                checkboxContainer.appendChild(checkboxInput);
                let checkboxLabel = document.createElement("label");
                checkboxLabel.setAttribute("for", this.serialNumber + checkBox.Id);
                checkboxLabel.innerHTML = checkBox.DisplayName;
                checkboxContainer.appendChild(checkboxLabel);

                inputs.push(checkboxInput);
            });
        });

        //check all the selected items - on edit
        if (this.configurationMode == "edit") {
            field.SelectedItem.forEach((selectedOption) => {
                checkboxesContainer.querySelector(
                    '[value="' + selectedOption.Id + '"]'
                ).checked = true;
            });
        }

        let multiSelectRightBox = document.createElement("div");
        multiSelectRightBox.classList.add("configurator__multiselect-wrapper-half");
        multiselectWrapper.appendChild(multiSelectRightBox);
        let rightBoxLabel = document.createElement("div");
        rightBoxLabel.classList.add("configurator__multiselect-label");
        rightBoxLabel.innerHTML = field.SelectedChildsLabel + '  (<span class="chosenOptionsCount">0</span>)';
        multiSelectRightBox.appendChild(rightBoxLabel);
        let chosenOptionsContainer = document.createElement("div");
        chosenOptionsContainer.classList.add("configurator__multiselect-chosenoptions");
        multiSelectRightBox.appendChild(chosenOptionsContainer);
        let emptyDescriptionRight = document.createElement("p");
        emptyDescriptionRight.classList.add("configurator__multiselect-description");
        emptyDescriptionRight.innerHTML = field.NoChildsSelectedLabel;
        chosenOptionsContainer.appendChild(emptyDescriptionRight);

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        this.setValidationOnField({
            field,
            htmlInput: inputs,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "input",
            validateFunction: (currentTarget) => {
                let isValid = false;
                inputs.forEach((input) => {
                    if (input.checked) {
                        isValid = true;
                    }
                });
                return isValid;
            }
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: inputs,
            controlToHide: container,
            triggerOnEvent: "input",
            getValueCallback: (currentField) => {
                return currentField.inputs.filter(x => x.checked).map(field => !field.dataset.usevaluefromcheckbox ? field.checked : field.value);
            },
            onVisiblityChange: (currentField, hidden) => {
                this.setValidationStateBasedOnVisibility(currentField, hidden)
            }
        });
        field.inputs = inputs;
        this.fields.push(field);
        var test1 = new Select(formSelect);
        var test2 = new Multiselect(container);
    }

    createDropDown(field, sectionContainer) {
        var container = document.createElement("div");
        container.classList.add("configurator__section__fields__subsection");
        sectionContainer.appendChild(container);

        var fieldLabel = document.createElement("label");
        var inputName = "DropDownListFormField-" + field.Id;
        fieldLabel.setAttribute("for", inputName);
        fieldLabel.innerHTML = field.DisplayName;
        container.appendChild(fieldLabel);

        let formSelect = document.createElement("div");
        formSelect.classList.add("form-select");
        formSelect.setAttribute("data-select", "");
        container.appendChild(formSelect);

        // select
        let selectEl = document.createElement("select");
        selectEl.setAttribute("id", "DropDownListFormField-" + field.Id);
        selectEl.setAttribute("data-fieldid", field.Id);
        formSelect.appendChild(selectEl);

        // select options
        // Default
        let defaultOption = document.createElement("option");
        defaultOption.setAttribute("value", "");
        defaultOption.setAttribute("selected", "selected");
        defaultOption.innerHTML = getDefaultSelectValue();
        selectEl.appendChild(defaultOption);

        const sectionID = sectionContainer.getAttribute('data-configurator-serial-no');
        updateSummaryOnInputChange({ field, input: selectEl, sectionID });

        field.ConfigurationFieldOptions.forEach((option) => {
            let optionEl = document.createElement("option");
            optionEl.setAttribute("value", option.Id);
            if (!isNullOrWhitespace(option.NavisionValue)) {
                optionEl.setAttribute("value", option.NavisionValue);
            } else if (!isNullOrWhitespace(option.FeatureKey)) {
                optionEl.setAttribute("value", option.FeatureKey);
            } else {
                optionEl.setAttribute("value", option.Name);
            }

            optionEl.innerHTML = option.DisplayName;
            optionEl.setAttribute("data-valuecode", option.Id);
            if (field.SelectedItem && this.configurationMode == "edit") {
                if (option.Id == field.SelectedItem.Id) {
                    defaultOption.removeAttribute("selected");
                    optionEl.setAttribute("selected", "selected");
                }
            }
            selectEl.appendChild(optionEl);
        });

        var validationMessage = document.createElement("div");
        validationMessage.classList.add('configurator__validation-msg');
        validationMessage.style.display = "none";
        container.appendChild(validationMessage);

        this.setValidationOnField({
            field,
            htmlInput: selectEl,
            htmlContainer: container,
            htmlValidationMessage: validationMessage,
            validateOnEvent: "change",
            validateFunction: (currentTarget) => {
                return currentTarget.value.length > 0;
            }
        });

        this.fieldsDependencyController.setDependencyOnField({
            field,
            htmlInput: selectEl,
            controlToHide: container,
            triggerOnEvent: "change",
            getValueCallback: (currentField) => currentField.inputs[0].value,
            onVisiblityChange: (currentField, hidden) => this.setValidationStateBasedOnVisibility(currentField, hidden)
        });

        field.inputs = [selectEl];
        this.fields.push(field);
        var test1 = new Select(formSelect);
    }

    getValues() {

        function getValue(field) {

            if (field.type === "checkbox") {
                return !field.dataset.usevaluefromcheckbox ? field.checked : field.value;
            }

            if (field.type === "file") {
                if (field.files.length > 0) {
                    return JSON.stringify({
                        filename: field.samefile ? '' : field.files[0].name,
                        // custom content property
                        content: field.samefile ? '' : field.files[0].content,
                        samefile: field.samefile
                    })
                } else {
                    return JSON.stringify({
                        filename: '',
                        // custom content property
                        content: '',
                        samefile: field.samefile
                    })
                }
            }

            if (field.nodeName === 'DIV') {
                return field.getAttribute('value');
            }
            return field.value

        }
        let values = [];
        this.fields.forEach((configuratorField) => {
            let inputs = configuratorField.inputs;
            inputs.forEach((field) => {
                if (
                    field.type === "checkbox" &&
                    !field.checked &&
                    field.dataset.usevaluefromcheckbox
                ) {
                    return;
                }
                let fieldData = {
                    FieldId: field.dataset.fieldid,
                    Value: configuratorField.dependency && configuratorField.dependency.hidden ? '' : getValue(field)
                };

                var existing = values.find((x) => x.FieldId === fieldData.FieldId);
                if (existing) {
                    existing.Value = existing.Value + "|" + fieldData.Value;
                } else {
                    values.push(fieldData);
                }
            });
            var existing = values.find((x) => x.FieldId === configuratorField.Id);
            if (!existing) {
                values.push({
                    FieldId: configuratorField.Id,
                    Value: ""
                });
            }
        });
        let returnData = {
            configuratorId: this.configuratorId,
            configurationMode: this.configurationMode, // TODO: Needs to be 'edit' if we are returning to the page from the cart for editing.
            action: this.configurationAction, // TODO: This has to be set, but don't appear to just be the name of the configurator?
            Data: {
                SerialNumber: this.serialNumber,
                Fields: values,
            },
        };

        return returnData;
    }

    getConfiguratorData(name) {
        return fetch("/api/config/ite/" + name + "?reset=true", {
            method: "GET",
            headers: { "Content-type": "application/json" },
        })
            .then((response) => response.json())
            .then((data) => {
                return data;
            });
    }

    getConfiguratorDataWithValue(configuratorId) {
        return fetch(
            "/api/config/GetConfiguratorByCartLineConfigurationGuid?configurationGuid=" +
            configuratorId,
            {
                method: "GET",
                headers: { "Content-type": "application/json" },
            }
        )
            .then((response) => response.json())
            .then((data) => {
                return data;
            });
    }
}
